import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import Card from './ui/Card';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

export function SiteAnalytics({ siteId }) {
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    
    setLoading(true);
    setError(null);
    
    api(`/sites/${siteId}/analytics?period=${period}`)
      .then((data) => {
        if (data.ok) {
          setAnalytics(data.data);
        } else {
          setError(data.error || 'Failed to load analytics');
        }
      })
      .catch((err) => {
        console.error('Analytics error:', err);
        setError('Failed to load analytics');
      })
      .finally(() => setLoading(false));
  }, [siteId, period]);

  const handleSendEmail = async () => {
    setSendingEmail(true);
    setEmailSuccess(false);
    
    try {
      const response = await api(`/sites/${siteId}/analytics/email`, {
        method: 'POST',
        body: JSON.stringify({ period })
      });
      
      if (response.ok) {
        setEmailSuccess(true);
        setTimeout(() => setEmailSuccess(false), 5000); // Clear success message after 5s
      } else {
        alert('Failed to send email report: ' + (response.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Email error:', err);
      alert('Failed to send email report');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="h-24 bg-gray-200 rounded"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-red-600">
          ⚠️ {error}
        </div>
      </Card>
    );
  }

  if (!analytics) return null;

  const periods = [
    { value: '24h', label: '24 Hours' },
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' }
  ];

  return (
    <Card className="p-6 bg-white dark:bg-gray-800">
      {/* Header with Period Selector */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          📊 Analytics
        </h3>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <div className="flex gap-2">
            {periods.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  period === p.value
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            onClick={handleSendEmail}
            disabled={sendingEmail}
            className={`px-3 py-1 text-sm rounded-md transition-colors flex items-center gap-2 ${
              emailSuccess
                ? 'bg-green-500 text-white'
                : 'bg-purple-500 hover:bg-purple-600 text-white disabled:bg-gray-400 disabled:cursor-not-allowed'
            }`}
          >
            {sendingEmail ? (
              <>
                <span className="animate-spin">⏳</span>
                Sending...
              </>
            ) : emailSuccess ? (
              <>
                <span>✅</span>
                Sent!
              </>
            ) : (
              <>
                <span>📧</span>
                Email Report
              </>
            )}
          </button>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Uptime Percentage */}
        <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg">
          <div className="text-sm text-green-700 dark:text-green-300 mb-1">
            Uptime
          </div>
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">
            {analytics.uptime_percentage}%
          </div>
          <div className="text-xs text-green-600 dark:text-green-400 mt-1">
            {analytics.total_checks} checks
          </div>
        </div>

        {/* Average Response Time */}
        <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg">
          <div className="text-sm text-blue-700 dark:text-blue-300 mb-1">
            Avg Response
          </div>
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
            {Math.round(analytics.avg_response_time)}ms
          </div>
          <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            {analytics.min_response_time}ms - {analytics.max_response_time}ms
          </div>
        </div>

        {/* Downtime Incidents */}
        <div className="p-4 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-lg">
          <div className="text-sm text-red-700 dark:text-red-300 mb-1">
            Incidents
          </div>
          <div className="text-3xl font-bold text-red-600 dark:text-red-400">
            {analytics.downtime_incidents}
          </div>
          <div className="text-xs text-red-600 dark:text-red-400 mt-1">
            Downtime events
          </div>
        </div>

        {/* Current Status */}
        <div className={`p-4 rounded-lg ${
          analytics.current_status?.status === 'UP'
            ? 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20'
            : 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20'
        }`}>
          <div className={`text-sm mb-1 ${
            analytics.current_status?.status === 'UP'
              ? 'text-green-700 dark:text-green-300'
              : 'text-red-700 dark:text-red-300'
          }`}>
            Current Status
          </div>
          <div className={`text-3xl font-bold ${
            analytics.current_status?.status === 'UP'
              ? 'text-green-600 dark:text-green-400'
              : 'text-red-600 dark:text-red-400'
          }`}>
            {analytics.current_status?.status || 'UNKNOWN'}
          </div>
          <div className={`text-xs mt-1 ${
            analytics.current_status?.status === 'UP'
              ? 'text-green-600 dark:text-green-400'
              : 'text-red-600 dark:text-red-400'
          }`}>
            Code: {analytics.current_status?.last_code || 'N/A'}
          </div>
        </div>
      </div>

      {/* Response Time Chart */}
      {analytics.recent_events && analytics.recent_events.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            📈 Response Time Trend
          </h4>
          <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart
                data={[...analytics.recent_events].reverse()}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="responseTimeGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis 
                  dataKey="timestamp" 
                  tickFormatter={(time) => new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  label={{ value: 'ms', angle: -90, position: 'insideLeft', style: { fill: '#6b7280' } }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#f3f4f6'
                  }}
                  labelFormatter={(time) => new Date(time).toLocaleString()}
                  formatter={(value) => [`${value}ms`, 'Response Time']}
                />
                <Area 
                  type="monotone" 
                  dataKey="response_time" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#responseTimeGradient)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Uptime Chart */}
      {analytics.recent_events && analytics.recent_events.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            🔄 Status History
          </h4>
          <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart
                data={[...analytics.recent_events].reverse()}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis 
                  dataKey="timestamp" 
                  tickFormatter={(time) => new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  domain={[0, 1]}
                  ticks={[0, 1]}
                  tickFormatter={(value) => value === 1 ? 'UP' : 'DOWN'}
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#f3f4f6'
                  }}
                  labelFormatter={(time) => new Date(time).toLocaleString()}
                  formatter={(value, name, props) => [
                    props.payload.status === 'UP' ? '✅ UP' : '❌ DOWN',
                    `Code: ${props.payload.code || 'N/A'}`
                  ]}
                />
                <Line 
                  type="stepAfter" 
                  dataKey={(entry) => entry.status === 'UP' ? 1 : 0}
                  stroke="#10b981" 
                  strokeWidth={3}
                  dot={{ fill: '#10b981', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Recent Events Timeline */}
      {analytics.recent_events && analytics.recent_events.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Recent Events ({analytics.recent_events.length})
          </h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {analytics.recent_events.map((event, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-md text-sm"
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`px-2 py-1 rounded text-xs font-semibold ${
                      event.status === 'UP'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}
                  >
                    {event.status}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">
                    Code: {event.code || 'N/A'}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">
                    {event.response_time}ms
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {new Date(event.timestamp).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Data Message */}
      {(!analytics.recent_events || analytics.recent_events.length === 0) && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No events recorded for this period. Site monitoring will begin shortly.
        </div>
      )}
    </Card>
  );
}
